<script setup lang="ts">
/**
 * Identity suggestions for one run-form input (MAR-129).
 *
 * An input that declared `constraints.entitySource.idType` is asking for a real-world identifier.
 * The context rail already knows which client or case is active, so this offers that scope's
 * matching identifiers instead of making the user go and copy a number out of a document.
 *
 * **Suggests, never fills.** Nothing is written until the user picks one. A prefilled field is a
 * field nobody reads — and the number it would prefill is the one thing in a legal document that
 * must not be silently wrong. Each suggestion shows where it came from for the same reason.
 *
 * Renders NOTHING when the scope is global, when the fetch fails, or when there is no match — which
 * is the common path, since most entities have no identifier at all. Degrading to a plain text
 * input is the correct behaviour, not an error state.
 */
import { formatIdentity } from '~~/shared/entities/vocabulary'
import type { EntityCandidate, EntityCandidatesResponse } from '~~/shared/entities/entityCandidate'

const props = defineProps<{
  /** The identifier category this input wants. */
  idType: string
  /** Current field value, so an already-chosen suggestion can be marked. */
  modelValue: unknown
}>()
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const { t, locale } = useI18n()
const { apiFetch } = useApi()
// The SCOPE, not the rail's payload. `useContextRail` derives its state from an async fetch, so a
// control that mounts with the page reads an empty rail and would silently offer nothing; the store
// is where the scope actually lives, and the rail follows it too.
const webChat = useWebChatStore()

const candidates = ref<EntityCandidate[]>([])
/**
 * Whether a query for the current scope has actually completed. Without it, "not asked yet" and
 * "asked and found nothing" look identical, and the empty-state line would flash on every mount.
 */
const resolved = ref(false)

/** The active scope, reduced to the one query parameter the endpoint takes. */
const scopeQuery = computed(() => {
  const scope = webChat.activeScope
  if (scope.kind === 'case') return { caseId: scope.id }
  if (scope.kind === 'client') return { clientId: scope.id }
  return null
})

async function load() {
  const query = scopeQuery.value
  if (!query) { candidates.value = []; return }
  try {
    const res = await apiFetch<EntityCandidatesResponse>('/api/entity-candidates', {
      query: { ...query, idType: props.idType },
    })
    candidates.value = res.candidates ?? []
    resolved.value = true
  } catch {
    // A suggestion is an accelerator. Losing it must not surface as an error on a form the user
    // can complete perfectly well by typing. `resolved` stays false so the empty-state line does
    // not claim "nothing matched" when the truth is "we never got an answer".
    candidates.value = []
  }
}

// The scope is restored from localStorage after hydration, so on a cold page load it is still
// `global` when this mounts. A short bounded retry catches it once it lands — bounded rather than a
// watcher, because this must not keep re-querying for the lifetime of the form.
onMounted(async () => {
  for (let attempt = 0; attempt < 5; attempt++) {
    if (scopeQuery.value) { await load(); return }
    await new Promise(resolve => setTimeout(resolve, 150))
  }
})

const label = (c: EntityCandidate) =>
  formatIdentity(c.idType, c.jurisdiction, c.idNumber, locale.value === 'en' ? 'en' : 'es') ?? c.idNumber ?? ''

/**
 * Say so when a scope IS active and nothing in it matched.
 *
 * Rendering nothing at all made "no match" indistinguishable from "the feature is broken" — which
 * is exactly how it read the first time someone opened a form whose scope held no company. Only
 * shown once a query has come back, and never under a global scope, where there is no scope that
 * could have matched and the line would be noise on every plain form.
 */
const emptyMessage = computed(() => {
  if (!resolved.value || candidates.value.length || !scopeQuery.value) return null
  return webChat.activeScope.kind === 'case'
    ? t('workflowRun.suggestions.noneInCase')
    : t('workflowRun.suggestions.noneInClient')
})

const source = (c: EntityCandidate) =>
  c.provenance.via === 'client'
    ? t('workflowRun.suggestions.fromClient')
    : t('workflowRun.suggestions.fromDocument', { name: c.provenance.documentName })
</script>

<template>
  <div v-if="candidates.length" class="mt-1.5 space-y-1">
    <p class="text-xs text-muted-foreground">{{ t('workflowRun.suggestions.title') }}</p>
    <div class="flex flex-wrap gap-1.5">
      <button
        v-for="c in candidates"
        :key="c.entityId"
        type="button"
        class="rounded-md border px-2 py-1 text-left text-xs transition-colors"
        :class="modelValue === c.idNumber
          ? 'border-accent-ink bg-accent-light'
          : 'border-border hover:border-accent-ink hover:bg-accent-lighter'"
        @click="emit('update:modelValue', c.idNumber ?? '')"
      >
        <span class="font-medium text-foreground">{{ c.name }}</span>
        <span class="block text-muted-foreground">{{ label(c) }}</span>
        <span class="block text-muted-foreground/70">{{ source(c) }}</span>
      </button>
    </div>
  </div>

  <p v-else-if="emptyMessage" class="mt-1.5 text-xs text-muted-foreground/70">{{ emptyMessage }}</p>
</template>
